import React, { useMemo, useRef, useState, useEffect } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { heroContent } from "../mock";
import { HighlightText } from "../components/ui/animated-reveal-text";
import { useModeContext } from "../contexts/ModeContext";

gsap.registerPlugin(useGSAP);

const itemsBC = [
  {
    id: "einzel",
    label: "Einzelcoaching*",
    image: "/einzelcoaching.jpg",
    desc: "Individuelle 1:1-Begleitung mit klaren Zielen und handfesten Tools für nachhaltige Verhaltensveränderung.",
  },
  {
    id: "gruppe",
    label: "Gruppencoaching*",
    image: "/gruppencoaching.jpg",
    desc: "Dynamik nutzen, Perspektiven erweitern: Team- und Gruppensettings mit Fokus auf Umsetzung im Alltag.",
  },
  {
    id: "keynote",
    label: "Keynote-Seminare*",
    image: "/keynote-seminare.jpg",
    desc: "Inspiration mit Substanz: kompakte Impulse, die zum Handeln motivieren – praxisnah und interaktiv.",
  },
];

const itemsPP = [
  {
    id: "executive",
    label: "Executive Coaching*",
    image: "/einzelcoaching.jpg",
    desc: "Strategische 1:1-Begleitung für Führungskräfte mit Fokus auf Performance-Optimierung und Führungsexzellenz.",
  },
  {
    id: "leadership",
    label: "Leadership Development*",
    image: "/gruppencoaching.jpg",
    desc: "Entwicklung von Führungskompetenzen und Teampotentialen für nachhaltige Höchstleistung im Unternehmen.",
  },
  {
    id: "speaking",
    label: "Keynote-Speaking*",
    image: "/keynote-seminare.jpg",
    desc: "Inspirierender Input für Peak Performance: praxisnahe Strategien für Unternehmen und Führungsteams.",
  },
];

const Kompetenzen = () => {
  const { activeMode } = useModeContext();
  const items = activeMode === 0 ? itemsBC : itemsPP;
  const sectionRef = useRef(null);
  const previewRef = useRef(null);
  const flipInnerRef = useRef(null);
  const [currentImage, setCurrentImage] = useState(items[0].image);
  const [activeItem, setActiveItem] = useState(items[0]);
  const [isFlipped, setIsFlipped] = useState(false);
  const [openMobileId, setOpenMobileId] = useState(null);

  const movers = useRef({ xTo: null, yTo: null });
  const clamp = (min, val, max) => Math.min(Math.max(val, min), max);

  // Update items when mode changes
  useEffect(() => {
    setCurrentImage(items[0].image);
    setActiveItem(items[0]);
    setIsFlipped(false);
    setOpenMobileId(null);
    // Reset flip to show back side (text) first
    if (flipInnerRef.current) {
      gsap.set(flipInnerRef.current, { rotateY: 0 });
    }
  }, [activeMode, items]);

  useGSAP(() => {
    if (!previewRef.current) return;
    gsap.set(previewRef.current, { autoAlpha: 0, scale: 0.96 });
    gsap.set(flipInnerRef.current, { transformStyle: "preserve-3d", rotateY: 0 });
    movers.current.xTo = gsap.quickTo(previewRef.current, "x", {
      duration: 0.2,
      ease: "sine.out",
    });
    movers.current.yTo = gsap.quickTo(previewRef.current, "y", {
      duration: 0.2,
      ease: "sine.out",
    });
  }, []);

  useGSAP(
    () => {
      if (!flipInnerRef.current) return;
      gsap.to(flipInnerRef.current, {
        rotateY: isFlipped ? 180 : 0,
        duration: 0.5,
        ease: "power2.inOut",
      });
    },
    { dependencies: [isFlipped] }
  );

  // Preload images once
  useMemo(() => {
    items.forEach((it) => {
      const img = new Image();
      img.src = it.image;
    });
  }, []);

  const handleMouseMove = (e) => {
    if (!movers.current.xTo || !movers.current.yTo || !previewRef.current) return;
    const margin = 12;
    const offset = 24; // Standard-Abstand neben dem Cursor

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const rect = previewRef.current.getBoundingClientRect();
    const estW = rect.width || 420;
    const estH = rect.height || 260;

    let tx = e.clientX + offset;
    let ty = e.clientY + offset;

    // Wenn rechts/unterhalb nicht genug Platz ist, neben/oberhalb positionieren
    if (tx + estW + margin > vw) tx = e.clientX - estW - offset;
    if (ty + estH + margin > vh) ty = e.clientY - estH - offset;

    // Final clamp innerhalb Viewport
    tx = clamp(margin, tx, vw - estW - margin);
    ty = clamp(margin, ty, vh - estH - margin);

    movers.current.xTo(tx);
    movers.current.yTo(ty);
  };

  const handleEnter = (item) => {
    setCurrentImage(item.image);
    setActiveItem(item);
    gsap.to(previewRef.current, { autoAlpha: 1, scale: 1, duration: 0.18, ease: "power2.out" });
  };

  const handleLeave = () => {
    gsap.to(previewRef.current, { autoAlpha: 0, scale: 0.96, duration: 0.18, ease: "power2.inOut" });
  };

  const renderLabel = (label) => {
    const hasStar = label.endsWith("*");
    const base = hasStar ? label.slice(0, -1) : label;
    return (
      <>
        {base}
        {hasStar ? (
          <sup className="ml-1 align-super text-[0.55em] tracking-tight text-[var(--brand-accent)]">*</sup>
        ) : null}
      </>
    );
  };

  const handleToggle = (item) => {
    setActiveItem(item);
    setCurrentImage(item.image);
    setIsFlipped((prev) => !prev);
  };

  return (
    <section
      id="kompetenzen"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="relative w-full h-auto md:h-auto md:min-h-[100svh] pt-[96px] md:pt-[80px] pb-8 md:pb-10 bg-white dark:bg-[#0B0B0C] overflow-visible"
    >
      <div style={{ 
        margin: '0 auto',
        padding: '0 24px',
        textAlign: 'left',
        display: 'block',
        width: '100%',
        maxWidth: '1280px',
        boxSizing: 'border-box'
      }}>
        <h2
          style={{ 
            position: 'relative',
            display: 'block',
            width: '100%',
            fontSize: 'clamp(2.2rem,9vw,6rem)',
            fontWeight: '600',
            letterSpacing: '-0.04em',
            color: activeMode === 0 ? '#A9BBB5' : 'rgb(229 229 229)',
            textAlign: 'left',
            margin: '0',
            padding: '0'
          }}
        >
          <span className="relative z-10">KOMPETENZEN</span>
        </h2>

        <div style={{ 
          marginTop: '16px',
          textAlign: 'left',
          display: 'block',
          width: '100%'
        }}>
          <HighlightText
            as="p"
            text={activeMode === 0 ? "Drei Wege, ein Ziel." : "Ein Ansatz, maximale Wirkung."}
            textClassName={`text-[clamp(0.95rem,2.2vw,1.1rem)] font-[600] ${activeMode === 0 ? 'text-white' : 'text-[#161616]'}`}
            highlightClassName={`rounded-md ${activeMode === 1 ? 'shine-loop' : ''}`}
            highlightColor="var(--brand-accent)"
            shineOnHover
            shineColor="rgba(255,255,255,0.55)"
            duration={1.0}
            ease="easeInOut"
            organic={false}
          />
        </div>

        {/* Intro text */}
        <div className="mt-6 mb-8 p-4 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 max-w-2xl" style={{ marginLeft: '0', marginRight: 'auto' }}>
          <p className={`${activeMode === 0 ? 'text-sm' : 'text-base'} text-neutral-700 dark:text-neutral-300 leading-relaxed`}>
            {activeMode === 0 
              ? "Jeder Ansatz ist darauf ausgelegt, echte und dauerhafte Veränderungen zu bewirken."
              : "Executive Coaching löst mentale, emotionale und strategische Blockaden und schafft die Klarheit, die nötig ist, um mit Fokus und Leichtigkeit zu wirken – statt nur zu leisten."
            }
          </p>
        </div>

        <ul className="mt-1 md:mt-0 divide-y-2 divide-[#C7D3D0] dark:divide-neutral-800" style={{ overflow: 'visible' }}>
          {items.map((item) => (
            <li key={item.id} className="select-none group" style={{ overflow: 'visible' }}>
              <button
                type="button"
                style={{ 
                  width: '100%',
                  padding: '32px 0',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'block',
                  color: activeMode === 0 ? '#A9BBB5' : 'rgb(229 229 229)',
                  transition: 'color 0.3s ease',
                  outline: 'none',
                  overflow: 'visible'
                }}
                onMouseEnter={() => handleEnter(item)}
                onMouseLeave={handleLeave}
                onFocus={() => handleEnter(item)}
                onBlur={handleLeave}
                onClick={() => {
                  // Desktop: Flip-Preview, Mobile: Accordion-Toggle
                  if (typeof window !== "undefined" && window.innerWidth >= 768) {
                    handleToggle(item);
                  } else {
                    setActiveItem(item);
                    setOpenMobileId((prev) => (prev === item.id ? null : item.id));
                  }
                }}
              >
                <span style={{ 
                  display: 'block',
                  fontSize: 'clamp(2.2rem,8.5vw,5rem)',
                  fontWeight: '600',
                  lineHeight: '0.95',
                  letterSpacing: '-0.02em',
                  textAlign: 'left',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  overflow: 'visible',
                  width: '100%',
                  maxWidth: '100%',
                  transition: 'transform 0.3s ease',
                  color: activeMode === 0 ? '#A9BBB5' : 'rgb(229 229 229)'
                }}>
                  {renderLabel(item.label)}
                </span>
              </button>

              {/* Mobile: aufklappbarer Inhalt */}
              {openMobileId === item.id ? (
                <div className="md:hidden pb-6">
                  <div className="grid grid-cols-2 gap-3 items-start">
                    <div className="rounded-lg overflow-hidden shadow ring-1 ring-black/5 dark:ring-white/10 bg-white dark:bg-neutral-900">
                      <img src={item.image} alt={item.label} className="w-full h-full object-cover" />
                    </div>
                    <div className="pl-1">
                      <div className="flex items-start gap-1.5 mb-1">
                        <span className="text-[var(--brand-accent)] font-bold leading-none">*</span>
                        <span className="text-sm text-neutral-700 dark:text-neutral-300">Kurzinfo</span>
                      </div>
                      <p className="text-[15px] leading-relaxed text-neutral-800 dark:text-neutral-300">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </div>

      {/* Cursor-Bild */}
      <div
        ref={previewRef}
        className="hidden md:block fixed left-0 top-0 z-[9999] pointer-events-none w-[320px] md:w-[420px] h-[200px] md:h-[260px]"
        style={{ transform: "translate3d(0,0,0)" }}
      >
        <div className={`w-full h-full rounded-lg shadow-2xl ring-1 ring-black/5 [perspective:1200px] ${activeMode === 1 ? 'bg-black/80' : 'bg-white'}`} style={{ overflow: 'visible' }}>
          <div ref={flipInnerRef} className="relative w-full h-full rounded-lg" style={{ transformStyle: "preserve-3d" }}>
            {/* Back: Text (shown first) */}
            <div
              className="absolute inset-0 grid place-items-center p-6 rounded-lg"
              style={{ 
                backfaceVisibility: "hidden", 
                transform: "rotateY(0deg)", 
                backgroundColor: activeMode === 1 ? "rgba(0, 0, 0, 0.9)" : "#0F0F10", 
                color: "#fff" 
              }}
            >
              <div className="max-w-sm text-left flex items-start gap-2">
                <span className="text-[var(--brand-accent)] font-bold text-xl leading-none">*</span>
                <p className="text-base md:text-lg leading-relaxed opacity-90">{activeItem?.desc}</p>
              </div>
            </div>
            {/* Front: Bild (shown after click) */}
            <div className="absolute inset-0 rounded-lg" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
              <img
                src={currentImage}
                alt="Kompetenz Vorschau"
                className="w-full h-full object-cover rounded-lg"
                draggable="false"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Kompetenzen;


